import { useEffect, useState } from 'react';
import axios from 'axios';
import { setBrandPhone } from '../lib/brand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public business contact details, as set in Admin → Settings → Business Info.
 *
 * Phone, email and address were hardcoded in the header, footer and several
 * pages, which meant the site kept publishing the previous owner's number
 * after a rebrand. This hook is the single read path so changing the number in
 * settings changes it everywhere.
 *
 * The response is cached at module level: the header and footer render on
 * every page, and without sharing one promise each mount would fire its own
 * request for a value that changes about once a year.
 */

const EMPTY = {
  _loaded: false,
  business_name: '',
  phone: '',
  email: '',
  website: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
};

let cache = null;
let inflight = null;
const subscribers = new Set();

const fetchBusinessInfo = () => {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;

  inflight = axios
    .get(`${API_URL}/api/settings/business`)
    .then((res) => {
      cache = { ...EMPTY, ...(res.data || {}), _loaded: true };
      // Non-React modules (seo.js structured data) read the number from the
      // brand bridge, so publish it there too.
      setBrandPhone(cache.phone);
      subscribers.forEach((fn) => fn(cache));
      return cache;
    })
    .catch(() => {
      // Contact details are decoration on most pages — a failure here must not
      // blank the header. Mark it loaded so consumers stop waiting and simply
      // render nothing where the number would go.
      cache = { ...EMPTY, _loaded: true };
      subscribers.forEach((fn) => fn(cache));
      return cache;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
};

/** Clear the cache so the next consumer refetches (used after an admin save). */
export const invalidateBusinessInfo = () => {
  cache = null;
};

export const useBusinessInfo = () => {
  const [info, setInfo] = useState(cache || EMPTY);

  useEffect(() => {
    let active = true;
    const onUpdate = (next) => {
      if (active) setInfo(next);
    };
    subscribers.add(onUpdate);
    fetchBusinessInfo().then(onUpdate);

    return () => {
      active = false;
      subscribers.delete(onUpdate);
    };
  }, []);

  return info;
};

/**
 * `(888) 990-0440` → `+18889900440`, for use in a tel: href.
 * Returns '' for an unset or unusable number so callers can skip the link
 * rather than render one that dials nothing.
 */
export const telHref = (phone) => {
  const digits = (phone || '').replace(/\D/g, '');
  if (digits.length < 10) return '';
  return `tel:+${digits.length === 10 ? '1' : ''}${digits}`;
};

export default useBusinessInfo;
